/**
 * WORKSPACE-SWITCHER-001 — workspaces derived from role + effective /auth/me
 * permissions. Purely navigational: a workspace never grants access; backend
 * RBAC (RBAC-PILOT-ORDERS-001 checks) stays authoritative.
 *
 * Same JWT / attendance session / idle tracking: switching is a route change.
 */
import { getRoleDashboardPath } from './roleDashboard';
import { isJurisdictionRequestor } from './jurisdictionRequestorGuard';

const SESSION_KEY = 'tf_active_workspace';
export const CHOOSER_PATH = '/choose-workspace';

// Roles whose primary home already IS the Dashboard (Order Desk) shell.
const DASHBOARD_SHELL_ROLES = new Set([
  'admin', 'order_desk', 'processor', 'tax_processor', 'title_search_processor',
  'search_processor', 'tax_search_team_leader', 'title_search_team_leader',
  'team_leader', 'qc_specialist', 'typing_processor', 'vendor_manager',
]);

const ROLE_LABELS = {
  qc_specialist: 'QC Specialist',
  sales_marketing: 'Sales & Marketing',
  jurisdiction_requestor: 'Jurisdiction Requestor',
  jurisdiction_manager: 'Jurisdiction Manager',
  hr_manager: 'HR Manager',
  hr_specialist: 'HR Specialist',
  hr: 'HR',
  it_manager: 'IT Manager',
  it_support: 'IT Support',
  accounting: 'Accounting',
};

const ROLE_DESCRIPTIONS = {
  jurisdiction_requestor: 'Submit and manage jurisdiction requests and approvals.',
  jurisdiction_manager: 'Maintain the jurisdiction directory and approvals.',
};

const hasPerm = (permissions, resource, action) =>
  Array.isArray(permissions) && permissions.some((p) => p.resource === resource && p.action === action);

// RBAC-WS-SEARCH-001/002 — matrix-granted processor workspaces. For QC Specialists the
// grant is effective only when their existing qc_product_scope (QC-SCOPE-001 SSOT)
// covers the workstream; the backend applies the same rule to /auth/me and every gate.
export const PROCESSOR_WORKSPACES = [
  {
    id: 'title_search', resource: 'title_search', qcScope: 'title', presentAs: 'search_processor',
    label: 'Title Search Processor', description: 'Work the Title Search orders assigned to you.',
    nativeRoles: new Set(['admin', 'search_processor', 'title_search_processor', 'title_search_team_leader']),
  },
  {
    id: 'tax_processor', resource: 'tax_search', qcScope: 'tax', presentAs: 'tax_processor',
    label: 'Tax Processor', description: 'Work the Tax Search orders assigned to you.',
    nativeRoles: new Set(['admin', 'processor', 'tax_processor', 'tax_search_team_leader']),
  },
];

const workstreamAllows = (user, ws) =>
  user?.role !== 'qc_specialist' || (Array.isArray(user?.qc_product_scope) && user.qc_product_scope.includes(ws.qcScope));

/**
 * Ordered list of workspaces the user can enter. First entry = primary role home.
 * @returns {Array<{id:string,label:string,description:string,home:string,shell:'admin'|'dashboard',capabilities?:object}>}
 */
export const deriveWorkspaces = (user, permissions = []) => {
  if (!user?.role) return [];
  const role = isJurisdictionRequestor(user) ? 'jurisdiction_requestor' : user.role;
  const primary = {
    id: role,
    label: ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    description: ROLE_DESCRIPTIONS[role] || 'Your primary workspace.',
    home: getRoleDashboardPath(role),
    shell: DASHBOARD_SHELL_ROLES.has(role) ? 'dashboard' : 'admin',
    primary: true,
  };
  const workspaces = [primary];

  // Orders pilot: matrix grant orders:view opens the Order Desk shell for
  // roles that do not already live there (additive; grants nothing itself).
  if (!DASHBOARD_SHELL_ROLES.has(role) && hasPerm(permissions, 'orders', 'view')) {
    workspaces.push({
      id: 'order_desk',
      label: 'Order Desk',
      description: 'View orders and use the Order capabilities granted to you.',
      home: '/dashboard/orders',
      shell: 'dashboard',
      capabilities: {
        view: true,
        create: hasPerm(permissions, 'orders', 'create'),
        update: hasPerm(permissions, 'orders', 'update'),
      },
    });
  }

  PROCESSOR_WORKSPACES.forEach((ws) => {
    if (ws.nativeRoles.has(role)) return;
    if (!hasPerm(permissions, ws.resource, 'access') || !workstreamAllows(user, ws)) return;
    workspaces.push({
      id: ws.id,
      label: ws.label,
      description: ws.description,
      home: '/dashboard/processor-orders',
      shell: 'dashboard',
      presentAs: ws.presentAs,
      capabilities: { access: true, process: hasPerm(permissions, ws.resource, 'process') && workstreamAllows(user, ws) },
    });
  });
  return workspaces;
};

export const getActiveWorkspace = () => {
  try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
};

export const setActiveWorkspace = (id) => {
  try { sessionStorage.setItem(SESSION_KEY, id); } catch { /* ignore */ }
};

// Called on login and logout so a fresh login shows the chooser again.
export const clearActiveWorkspace = () => {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
};

// WS-CONTEXT-001 — shared personal/team areas (ModuleShell) belong to no workspace:
// they preserve the last displayed workspace instead of inferring one from the URL.
export const WORKSPACE_NEUTRAL_PREFIXES = ['/analytics', '/reports', '/dashboard/workspace', '/dashboard/team', '/team/', '/training/whiteboard', '/hr/breaks', '/my-quality-records'];
export const isWorkspaceNeutralPath = (pathname) => Boolean(pathname) && WORKSPACE_NEUTRAL_PREFIXES.some((p) => {
  const base = p.replace(/\/$/, '');
  return pathname === base || pathname.startsWith(`${base}/`);
});

const remember = (ws) => {
  if (ws && getActiveWorkspace() !== ws.id) setActiveWorkspace(ws.id);
  return ws;
};

/** Active workspace = the shell currently displayed (remembered); neutral paths keep the last one, primary as fallback. */
export const resolveActiveWorkspace = (workspaces, pathname) => {
  if (pathname && !isWorkspaceNeutralPath(pathname)) {
    // Longest matching `home` prefix wins (e.g. /dashboard/processor-orders over /dashboard).
    const homeMatches = workspaces
      .filter((w) => w.home && (pathname === w.home || pathname.startsWith(`${w.home}/`)))
      .sort((a, b) => b.home.length - a.home.length);
    if (homeMatches.length) {
      // Several workspaces can share a home (Title Search + Tax Processor) → stored choice breaks the tie.
      const storedId = getActiveWorkspace();
      return remember(homeMatches.find((w) => w.id === storedId && w.home === homeMatches[0].home) || homeMatches[0]);
    }
    const byPath = workspaces.find((w) => w.shell === 'dashboard'
      ? pathname.startsWith('/dashboard')
      : pathname.startsWith('/admin'));
    if (byPath) return remember(byPath);
  }
  const stored = getActiveWorkspace();
  return workspaces.find((w) => w.id === stored) || workspaces[0] || null;
};

/** WS-CONTEXT-002 — "Back to <workspace>" sidebar link for neutral pages: the preserved
 * active workspace's module home. Null when a nav item already points there (no duplicates). */
export const backToWorkspaceLink = (workspaces, pathname, existingHomes = []) => {
  const active = resolveActiveWorkspace(workspaces, pathname);
  if (!active?.home || existingHomes.includes(active.home)) return null;
  return { id: active.id, label: `Back to ${active.label}`, to: active.home };
};

/** Where to send the user right after login. */
export const postLoginRoute = (user, permissions, roleRoute) => {
  const workspaces = deriveWorkspaces(user, permissions);
  if (workspaces.length > 1 && !getActiveWorkspace()) return CHOOSER_PATH;
  return roleRoute;
};
